import React from 'react';

function HelpShell({ title, children }) {
  return (
    <div className="help-page">
      <div className="help-header">
        <a href="/app" className="help-logo">SQE<span className="help-logo-accent">Track</span></a>
      </div>
      <div className="help-content">
        <h1 className="help-title">{title}</h1>
        {children}
        <div className="help-footer">
          <a href="/app?tab=mcqresults">← Back to Import MCQ Results</a>
        </div>
      </div>
    </div>
  );
}

function TroubleTable({ rows }) {
  return (
    <div className="help-trouble-table">
      <div className="help-trouble-head">
        <span>What you see</span>
        <span>What happened</span>
        <span>Fix</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="help-trouble-row">
          <span className="help-trouble-msg">"{r[0]}"</span>
          <span>{r[1]}</span>
          <span>{r[2]}</span>
        </div>
      ))}
    </div>
  );
}

export function HelpImportBarbri() {
  return (
    <HelpShell title="Importing your BARBRI results into SQETrack">
      <p className="help-intro">
        SQETrack can pull your topic-level scores from BARBRI practice question sets. You copy the results page, paste it into SQETrack, and your scores are mapped to SQETrack topics automatically. The whole process takes about two minutes.
      </p>

      <h2>What you need</h2>
      <ul>
        <li>A BARBRI SQE1 Prep account</li>
        <li>At least one completed practice question set</li>
      </ul>

      <h2>Step 1 — Open your practice set results</h2>
      <p>
        Log in to BARBRI. Go to <strong>My Assignments</strong>. Find a completed practice question set (not a lecture or reading assignment) and click <strong>Assignment Details</strong>.
      </p>
      <p>
        You should see your overall score at the top, followed by subject areas (Criminal Law, Trusts, Criminal Practice, etc.) with percentage scores.
      </p>
      <div className="help-screenshot-placeholder">
        [Screenshot: BARBRI results page showing collapsed subject sections]
      </div>

      <h2>Step 2 — Expand every subject section</h2>
      <p>
        Click the ▼ arrow next to each subject name. The section expands to show individual topics with their own scores.
      </p>
      <p>
        You need to expand every section. If a section is collapsed, those topics will be missing from your import.
      </p>
      <div className="help-screenshot-placeholder">
        [Screenshot: BARBRI results page with one section expanded showing individual topic scores]
      </div>

      <h2>Step 3 — Select and copy the entire page</h2>
      <p>
        Press <kbd>Ctrl+A</kbd> (or <kbd>Cmd+A</kbd> on Mac) to select everything on the page. Then press <kbd>Ctrl+C</kbd> (or <kbd>Cmd+C</kbd>) to copy.
      </p>
      <p>
        You will be copying navigation menus, headers, and other page elements along with your scores. This is fine — SQETrack ignores everything except topic scores.
      </p>

      <h2>Step 4 — Paste into SQETrack</h2>
      <p>
        Go to <a href="/app?tab=mcqresults">Import MCQ Results</a> in SQETrack. Select <strong>BARBRI</strong> as your provider. Click in the paste box and press <kbd>Ctrl+V</kbd> (or <kbd>Cmd+V</kbd>). Click <strong>Parse Results</strong>.
      </p>
      <div className="help-screenshot-placeholder">
        [Screenshot: SQETrack import page with BARBRI selected]
      </div>

      <h2>Step 5 — Review and confirm</h2>
      <p>
        SQETrack shows a preview of matched topics. Green rows matched automatically. Amber rows need you to check the match. Red rows need you to pick the correct topic from a dropdown.
      </p>
      <p>Once you are happy, click <strong>Save</strong>.</p>
      <p>
        Each BARBRI practice set is stored separately, so you can import multiple sets. If SQETrack recognises that you have already imported the same set, it will block the import and tell you the date it was originally saved. Remove the existing import from Import History first if you want to re-import it.
      </p>
      <div className="help-screenshot-placeholder">
        [Screenshot: SQETrack preview table]
      </div>

      <h2>Troubleshooting</h2>
      <TroubleTable rows={[
        [
          'We can see your subject scores but not the individual topics',
          'Subject sections not expanded',
          'Go back to BARBRI, expand every section (Step 2), copy again',
        ],
        [
          'This looks like your assignments page',
          'Copied from the assignments list, not a practice set result',
          'Open a specific practice question set result (Step 1), copy again',
        ],
        [
          'We only found a few topics',
          'Some sections still collapsed',
          'Expand all sections, Ctrl+A, copy again',
        ],
        [
          "We couldn't find any results",
          'Copied from the wrong page',
          'Start from Step 1',
        ],
        [
          'This looks like a set you\'ve already imported on [date]',
          'Same practice set imported before',
          'Go to Import History, remove the previous import, then save again',
        ],
      ]} />

      <h2>Removing an import</h2>
      <p>
        If something went wrong, go to <a href="/app?tab=mcqresults">Import MCQ Results</a> and scroll to Import History. Click <strong>Remove</strong> next to the import. Your topic scores will revert to what they were before.
      </p>
    </HelpShell>
  );
}

export function HelpImportLawDrills() {
  return (
    <HelpShell title="Importing your Law Drills results into SQETrack">
      <p className="help-intro">
        SQETrack can pull your topic-level scores from Law Drills. You copy the results page, paste it into SQETrack, and your scores are mapped to SQETrack topics automatically. The whole process takes about two minutes.
      </p>

      <h2>What you need</h2>
      <ul>
        <li>A Law Drills account (SQE1 course)</li>
        <li>At least one completed drill or multichoice set</li>
      </ul>

      <h2>Step 1 — Open your results page</h2>
      <p>
        Log in to Law Drills and navigate to your results page for the relevant FLK module (FLK1 or FLK2).
      </p>
      <div className="help-screenshot-placeholder">
        [Screenshot: Law Drills results page]
      </div>

      <h2>Step 2 — Select All-time</h2>
      <p>
        At the top of the results page, you will see period options: Day, Week, Month, All-time. Click <strong>All-time</strong>.
      </p>
      <p>
        This gives SQETrack your cumulative scores across every session. If you leave it on Day, Week, or Month, you will only get partial data.
      </p>
      <div className="help-screenshot-placeholder">
        [Screenshot: Law Drills period selector with All-time highlighted]
      </div>

      <h2>Step 3 — Expand every subject section</h2>
      <p>
        Click the ▶ arrow next to each subject to expand it. Subtopics appear underneath, each with their own score or a dash (no questions attempted for that subtopic).
      </p>
      <p>Expand every section, even subjects where you have not done many questions.</p>
      <div className="help-screenshot-placeholder">
        [Screenshot: Law Drills with expanded sections showing subtopics]
      </div>

      <h2>Step 4 — Select and copy the entire page</h2>
      <p>
        Press <kbd>Ctrl+A</kbd> (or <kbd>Cmd+A</kbd> on Mac) to select everything. Press <kbd>Ctrl+C</kbd> (or <kbd>Cmd+C</kbd>) to copy.
      </p>

      <h2>Step 5 — Paste into SQETrack</h2>
      <p>
        Go to <a href="/app?tab=mcqresults">Import MCQ Results</a> in SQETrack. Select <strong>Law Drills</strong>. Paste into the box and click <strong>Parse Results</strong>.
      </p>

      <h2>Step 6 — Review and confirm</h2>
      <p>Check the preview, fix any mismatches, click <strong>Save</strong>.</p>
      <p>
        If you already have a Law Drills import, SQETrack will show a notice before you save. Saving will replace your previous import with the new one — your old scores will be removed and replaced with the updated All-time data.
      </p>

      <h2>Troubleshooting</h2>
      <TroubleTable rows={[
        [
          "It looks like you're on the Day, Week, or Month view",
          "Didn't select All-time",
          'Go back, click All-time (Step 2), copy again',
        ],
        [
          "We can see your subject scores but not the detailed topic breakdown",
          'Sections not expanded',
          'Expand every subject (Step 3), copy again',
        ],
        [
          'No scores found',
          'No questions completed for this FLK module',
          'Do some practice questions first',
        ],
        [
          "We couldn't find any results",
          'Wrong page',
          'Start from Step 1',
        ],
      ]} />

      <h2>Removing an import</h2>
      <p>
        Go to <a href="/app?tab=mcqresults">Import History</a>, click <strong>Remove</strong>. Because Law Drills is All-time data, you only ever have one active import — saving a new one replaces the old automatically.
      </p>
    </HelpShell>
  );
}
